/**
 * Input Sanitization Utilities
 * ============================
 * 
 * This module provides centralized input sanitization functions used across
 * patient and provider setup forms. These functions filter user input in
 * real-time to prevent invalid characters from being entered into specific
 * field types, providing immediate feedback and preventing data quality
 * issues at the source.
 * 
 * Usage:
 * ------
 * import { sanitizeNameInput, sanitizeEmailInput } from '../../utils/inputSanitization';
 * 
 * // In an onChange handler:
 * onChange={(e) => {
 *     const sanitizedValue = sanitizeNameInput(e.target.value);
 *     handleInputChange('firstName', sanitizedValue);
 * }}
 */

/**
 * Sanitizes name input to only allow characters typically found in human names.
 * 
 * Allowed characters:
 * - Letters (including accented/international characters like é, ñ, ü, etc.)
 * - Spaces (for compound names like "Mary Jane")
 * - Hyphens (for hyphenated names like "Jean-Pierre")
 * - Apostrophes (for names like "O'Brien" or "D'Angelo")
 * 
 * Design decisions:
 * - We use Unicode property escapes (\p{L}) to support international names
 *   without needing to enumerate every possible accented character.
 * - The 'u' flag enables Unicode support in the regex.
 * - We intentionally allow spaces, hyphens, and apostrophes as these are
 *   commonly found in legitimate names across cultures.
 * 
 * @param {string} value - The raw input value from a name field
 * @returns {string} - The sanitized value with invalid characters removed
 * 
 * @example
 * sanitizeNameInput("John123") // Returns "John"
 * sanitizeNameInput("O'Brien") // Returns "O'Brien" (apostrophe allowed)
 * sanitizeNameInput("Jean-Pierre") // Returns "Jean-Pierre" (hyphen allowed)
 * sanitizeNameInput("José García") // Returns "José García" (accented chars allowed)
 */
export const sanitizeNameInput = (value) => {
    if (!value) return value;
    // Regex allows: Unicode letters (\p{L}), spaces, hyphens, and apostrophes
    // The 'u' flag enables Unicode property escapes for international character support
    return value.replace(/[^\p{L}\s'-]/gu, '');
};

/**
 * Sanitizes email input to only allow characters valid in email addresses.
 * 
 * Allowed characters:
 * - Letters (a-z, A-Z)
 * - Numbers (0-9)
 * - Special characters commonly used in emails: @ . _ - + '
 * 
 * Design decisions:
 * - This is a permissive filter that blocks obviously invalid characters
 *   (like #, $, %, ^, &, *, !, etc.) while still allowing the user to type
 *   their email address naturally.
 * - Full email format validation (checking for proper structure like user@domain.tld)
 *   happens separately on blur/submit via the validateEmail function.
 * - We include the '+' character to support email aliases (e.g., user+tag@gmail.com)
 *   which are commonly used for account management and testing.
 * - We include the apostrophe (') as it is a valid character in email addresses
 *   per RFC 5321
 * 
 * @param {string} value - The raw input value from an email field
 * @returns {string} - The sanitized value with invalid characters removed
 * 
 * @example
 * sanitizeEmailInput("test@example.com") // Returns "test@example.com"
 * sanitizeEmailInput("test#$%@example.com") // Returns "test@example.com"
 * sanitizeEmailInput("user+tag@gmail.com") // Returns "user+tag@gmail.com" (+ allowed)
 */
export const sanitizeEmailInput = (value) => {
    if (!value) return value;
    // Allow: letters, numbers, @, ., _, -, +, '
    // These are the characters commonly found in valid email addresses
    // Apostrophe is valid per RFC 5321 for the local part of email addresses
    return value.replace(/[^a-zA-Z0-9@._\-+']/g, '');
};
